// crewbit-admin/src/components/money/TaxBoxStrip.tsx
//
// Hero 4-cell horizontal strip below the page header. Admin variant of
// mobile's TaxBoxBreakdown (vertical card-list). Same data, different chrome.
//
// Branches on tax_mode:
//   - 'charge'       → Box 101 / Box 105 / Box 108 / Box 109 (net = emphasis)
//   - 'quick_method' → Revenue (Box 101) / QM rate / QM credit / Net (Box 109)
//   - 'exempt'       → strip hidden (parent renders SetupHstBanner instead)
//
// Reads from TaxSummaryDetailEnvelope. Wraps to 2-cell pairs on narrow widths.
// Skeleton render when detail is null (loading first paint).
//
// Spec §Tax.7.2 + §3b LOCKED.

import type { TaxSummaryDetailEnvelope } from '../../hooks/useTaxSummaryDetail';
import { palette, radii, spacing } from '../../styles/tokens';
import { TaxBoxCell } from './TaxBoxCell';

interface TaxBoxStripProps {
  detail: TaxSummaryDetailEnvelope | null;
  isLoading?: boolean;
}

export function TaxBoxStrip({ detail, isLoading = false }: TaxBoxStripProps) {
  if (!detail && isLoading) {
    return <TaxBoxStripSkeleton />;
  }
  if (!detail) return null;
  if (detail.tax_mode === 'exempt') return null;

  const isQuickMethod = detail.tax_mode === 'quick_method';

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing.sm,
        marginBottom: spacing.lg,
      }}
    >
      {isQuickMethod ? (
        <>
          <TaxBoxCell
            label="Revenue"
            amount={detail.box_101_revenue ?? 0}
            description="Eligible quick-method revenue YTD."
          />
          <TaxBoxCell
            label="QM rate"
            amount={null}
            valueOverride={`${((detail.quick_method_remittance_rate ?? 0) * 100).toFixed(1)}%`}
            description="Remittance rate based on your industry."
          />
          <TaxBoxCell
            label="QM credit"
            amount={detail.quick_method_credit ?? 0}
            description="1% credit on first $30k of revenue."
          />
          <TaxBoxCell
            label="Net (Box 109)"
            amount={detail.box_109_net ?? 0}
            description="What you owe CRA this period."
            tone="emphasis"
          />
        </>
      ) : (
        <>
          <TaxBoxCell
            label="Box 101"
            amount={detail.box_101_revenue ?? 0}
            description="Total taxable revenue."
          />
          <TaxBoxCell
            label="Box 105"
            amount={detail.box_105_hst_collected ?? 0}
            description="HST collected from clients."
          />
          <TaxBoxCell
            label="Box 108"
            amount={detail.box_108_itcs ?? 0}
            description="Input tax credits from expenses."
          />
          <TaxBoxCell
            label="Box 109"
            amount={detail.box_109_net ?? 0}
            description="Net owing to CRA."
            tone="emphasis"
          />
        </>
      )}
    </div>
  );
}

function TaxBoxStripSkeleton() {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: spacing.sm,
        marginBottom: spacing.lg,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            flex: 1,
            minWidth: 0,
            height: 96,
            background: palette.g50,
            border: `1px solid ${palette.g150}`,
            borderRadius: radii.md,
            padding: spacing.md,
          }}
          aria-hidden="true"
        >
          <div style={{ width: 60, height: 10, background: palette.g100, borderRadius: 4, marginBottom: 10 }} />
          <div style={{ width: 100, height: 18, background: palette.g100, borderRadius: 4 }} />
        </div>
      ))}
      <span style={{ position: 'absolute', left: -9999 }}>Loading tax summary…</span>
    </div>
  );
}
